import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  GlobalStyles,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches } from "src/api/generic";
import {
  useGetItemMovementReportData,
  useGetItemsReport,
  type ItemMovementReportDataPayload,
  type ItemMovementReportRow,
} from "src/api/reports";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
  reportLayoutTokens,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import FilterDrawer, {
  type ItemMovementRowFilters,
} from "src/pages/reports/item-movement/filters";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";

type ItemMovementFilters = {
  startDate: string;
  endDate: string;
  branchIds: number[];
};

const parseFilters = (sp: URLSearchParams): ItemMovementFilters => {
  const b = sp.getAll("branches");
  return {
    startDate:
      sp.get("start_date") || moment().startOf("month").format("yyyy-MM-DD"),
    endDate: sp.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
    branchIds: b.length
      ? b.map((x) => Number(x)).filter((n) => Number.isFinite(n))
      : [],
  };
};

const emptyRowFilters = (): ItemMovementRowFilters => ({
  name: "",
  sku: "",
  itemType: "all",
  transactionReference: "",
});

const NUMERIC_COLUMNS = new Set(["qty", "cost"]);

const escapeCsvCell = (value: unknown): string => {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const buildCsvFromRows = (
  cols: { key: string; label: string }[],
  dataRows: ItemMovementReportRow[]
): string => {
  const header = cols.map((c) => escapeCsvCell(c.label)).join(",");
  const body = dataRows.map((row) =>
    cols
      .map((c) => {
        const v = row[c.key as keyof ItemMovementReportRow];
        if (typeof v === "number") return String(v);
        return escapeCsvCell(v ?? "");
      })
      .join(",")
  );
  return [header, ...body].join("\n");
};

const formatNumber = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const ItemMovementReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<ItemMovementFilters>(() =>
    parseFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<ItemMovementFilters>(() =>
    parseFilters(new URLSearchParams(searchParams.toString()))
  );
  const [appliedRowFilters, setAppliedRowFilters] =
    useState<ItemMovementRowFilters>(emptyRowFilters);
  const [filterOpen, setFilterOpen] = useState(false);

  const { startDate, endDate, branchIds } = appliedFilters;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((br) =>
      specificBranchHasPermission("download_third_eye_report", br.id)
    ) ?? [];

  const { data: reportBody, isFetching, refetch } = useGetItemMovementReportData(
    {
      start_date: startDate,
      end_date: endDate,
      branches: branchIds.length > 0 ? branchIds : undefined,
      enabled: true,
    }
  );

  const { refetch: refetchDownload, isFetching: isDownloadLoading } =
    useGetItemsReport({
      start_date: startDate,
      end_date: endDate,
      ...(branchIds.length > 0 ? { branches: branchIds } : {}),
      ...(appliedRowFilters.name ? { name: appliedRowFilters.name } : {}),
      ...(appliedRowFilters.sku ? { sku: appliedRowFilters.sku } : {}),
      ...(appliedRowFilters.itemType && appliedRowFilters.itemType !== "all"
        ? { method: appliedRowFilters.itemType }
        : {}),
      ...(appliedRowFilters.transactionReference
        ? { transaction_reference: appliedRowFilters.transactionReference }
        : {}),
    });

  const downloadReportsPermission = oneBranchHasPermission(
    "download_third_eye_report"
  );

  const payload = (
    reportBody as { data?: ItemMovementReportDataPayload } | null | undefined
  )?.data;

  const columns = payload?.columns ?? [];
  const rows = payload?.rows ?? [];

  const itemTypeOptions = useMemo(() => {
    const types = new Set<string>();
    rows.forEach((r) => {
      const type = r.transaction_type?.trim();
      if (type) types.add(type);
    });
    return Array.from(types).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    const name = appliedRowFilters.name.trim().toLowerCase();
    const sku = appliedRowFilters.sku.trim().toLowerCase();
    const itemType = appliedRowFilters.itemType;
    const ref = appliedRowFilters.transactionReference.trim().toLowerCase();
    return rows.filter((r) => {
      if (name && !(r.item_name ?? "").toLowerCase().includes(name)) return false;
      if (sku && !(r.sku ?? "").toLowerCase().includes(sku)) return false;
      if (itemType && itemType !== "all") {
        if (itemType === "direct" || itemType === "indirect") {
          if (r.method !== itemType) return false;
        } else if ((r.transaction_type ?? "") !== itemType) {
          return false;
        }
      }
      if (ref && !(r.transaction_reference ?? "").toLowerCase().includes(ref)) {
        return false;
      }
      return true;
    });
  }, [rows, appliedRowFilters]);

  const periodLabel = useMemo(
    () =>
      t("reportDateRange", {
        start: moment(startDate).format("D MMM YYYY"),
        end: moment(endDate).format("D MMM YYYY"),
      }),
    [t, startDate, endDate]
  );

  const syncAppliedToUrl = useCallback(
    (next: ItemMovementFilters) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      next.branchIds.forEach((id) => p.append("branches", String(id)));
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    syncAppliedToUrl(draftFilters);
    void refetch();
  };

  const handlePrint = () => window.print();

  const handleDownload = useCallback(async () => {
    try {
      const result = await refetchDownload();
      const d = result.data;
      if (typeof d === "string" && d.trim().length > 0) {
        downloadAsXLS(`\uFEFF${d}`, "item-movement-report");
        return;
      }
    } catch {
      // Fall back to the currently visible (filtered) table.
    }
    const csv = buildCsvFromRows(columns, filteredRows);
    if (csv.trim().length > 0) {
      downloadAsXLS(`\uFEFF${csv}`, "item-movement-report");
    }
  }, [refetchDownload, columns, filteredRows]);

  const cellValue = (columnKey: string, row: ItemMovementReportRow) => {
    const v = row[columnKey as keyof ItemMovementReportRow];
    if (NUMERIC_COLUMNS.has(columnKey)) {
      return typeof v === "number" ? formatNumber(v) : v ?? "—";
    }
    return v == null || v === "" ? "—" : v;
  };

  const minTableWidth = Math.max(columns.length * 140, 1100);

  const branchSelectControl = (
    value: ItemMovementFilters["branchIds"],
    onChange: (ids: number[]) => void
  ) => (
    <FormControl
      size="small"
      sx={{
        minWidth: 220,
        flexShrink: 0,
        alignSelf: "flex-start",
        "& .MuiOutlinedInput-root": { minHeight: 40 },
      }}
    >
      <InputLabel id="item-mv-branches" shrink>
        {generalT("branch")}
      </InputLabel>
      <Select<string[]>
        labelId="item-mv-branches"
        multiple
        value={value.map((id) => String(id))}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          const next = typeof v === "string" ? v.split(",") : v;
          const strs = next as string[];
          if (strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)) {
            onChange([]);
            return;
          }
          const ids = strs.map(Number).filter((n) => n > 0);
          onChange(ids);
        }}
        input={<OutlinedInput label={generalT("branch")} />}
        renderValue={(selected) => {
          if (selected.length === 0) return t("allBranches");
          return selected
            .map(
              (idStr) =>
                branchesHavePermission.find((b) => String(b.id) === idStr)
                  ?.name ?? idStr
            )
            .join(", ");
        }}
      >
        <MenuItem
          key="__all-branches-item-mv"
          value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
          sx={{
            borderBottom: "1px solid #EAECF0",
            bgcolor: "#F9FAFB",
          }}
        >
          <Typography fontWeight={600} variant="body2" component="span">
            {t("allBranches")}
          </Typography>
        </MenuItem>
        {branchesHavePermission.map((b) => (
          <MenuItem key={b.id} value={String(b.id)}>
            <Checkbox size="small" checked={value.includes(b.id)} />
            <ListItemText primary={b.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeControl = (
    draft: ItemMovementFilters,
    setDraft: Dispatch<SetStateAction<ItemMovementFilters>>
  ) => (
    <Box sx={{ minWidth: 250 }}>
      <DateRangeInput
        startDate={draft.startDate ? moment(draft.startDate) : null}
        startDateId="item-mv-start"
        endDate={draft.endDate ? moment(draft.endDate) : null}
        endDateId="item-mv-end"
        onDatesChange={(arg: {
          startDate: moment.Moment | null;
          endDate: moment.Moment | null;
        }) => {
          const s = arg.startDate?.format("yyyy-MM-DD");
          const e = arg.endDate?.format("yyyy-MM-DD");
          if (s && e) {
            setDraft((prev) => ({ ...prev, startDate: s, endDate: e }));
          }
        }}
      />
    </Box>
  );

  return (
    <Box
      p={{ xs: 2, sm: "12px 32px 32px" }}
      maxWidth="100%"
      sx={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#item-movement-print-root, #item-movement-print-root *": {
              visibility: "visible",
            },
            "#item-movement-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("ItemMovementReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              {branchSelectControl(draftFilters.branchIds, (ids) =>
                setDraftFilters((p) => ({ ...p, branchIds: ids }))
              )}
              {dateRangeControl(draftFilters, setDraftFilters)}
            </>
          }
          center={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={() => setFilterOpen(true)}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadLoading}
                disabled={!downloadReportsPermission}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <FilterDrawer
        open={filterOpen}
        handleClose={() => setFilterOpen(false)}
        filters={appliedRowFilters}
        onApply={setAppliedRowFilters}
        itemTypeOptions={itemTypeOptions}
      />

      <Box id="item-movement-print-root">
        <ReportPageHeading
          title={t("ItemMovementReport")}
          subtitle={companyName ?? payload?.company_name}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {isFetching && !payload ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table
                size="small"
                sx={{ minWidth: minTableWidth, tableLayout: "auto" }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: reportLayoutTokens.tableHeaderBg,
                        color: reportLayoutTokens.tableHeaderText,
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        borderBottom: reportLayoutTokens.cardBorder,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={NUMERIC_COLUMNS.has(col.key) ? "center" : "left"}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ position: "relative", zIndex: 1 }}>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(columns.length, 1)}
                        align="center"
                        sx={{
                          py: 4,
                          color: "text.secondary",
                          fontSize: "0.9rem",
                        }}
                      >
                        {rows.length === 0
                          ? generalT("noData")
                          : t("noRowsMatchFilter")}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {filteredRows.map((row, idx) => (
                    <TableRow
                      key={`${row.transaction_reference}-${row.transaction_type}-${idx}`}
                      sx={{
                        "& td": {
                          borderColor: "#EEF0F2",
                          fontSize: "0.875rem",
                          color: "#101828",
                          py: 1,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          align={
                            NUMERIC_COLUMNS.has(col.key) ? "center" : "left"
                          }
                        >
                          {cellValue(col.key, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default ItemMovementReportPage;
